import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
  Chip, Box, Stack, Button, FormHelperText, Typography,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  QuizOutlined as QuizOutlinedIcon,
  LinkOutlined as LinkOutlinedIcon,
} from '@mui/icons-material';
import JsonExportConfirmModal from '../../../Component/Modal/JsonExportConfirmModal';
import { AuthContext } from '../../../context/auth-context';
import { ENTITY_LABELS } from '../../../utils/labelMapping';

const MONITORING_METRIC_LABELS = [
  ENTITY_LABELS.rank,
  'Sentiment',
  ENTITY_LABELS.purchaseCriteria,
  ENTITY_LABELS.brandPillar,
  ENTITY_LABELS.brandAttributes,
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 250 },
  },
};

// Parse MM-DD-YYYY to Date for sorting
const parseDate = (str) => {
  if (!str) return new Date(0);
  const [m, d, y] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export default function MonitoringReportModal({
  open,
  onClose,
  onGenerate,
  onGenerateJson,
  loading = false,
  jsonLoading = false,
  brandName,
  brandItems = [],
  categories = [],
  personas = [],
  markets = [],
  geography = [],
  dbLlms = [],
  customerLlm = [],
  availableDates = [],
  defaults = {},
  brandFilters = [],
}) {
  const { user } = useContext(AuthContext);
  const isAdmin =
    user?.user_role?.role?.name === 'Company admin' ||
    user?.user_role?.role?.name === 'Super User';

  const sortedDates = useMemo(
    () => [...availableDates].sort((a, b) => parseDate(b) - parseDate(a)),
    [availableDates]
  );

  // ── Form state ─────────────────────────────────────────────────────────────
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [selectedGeos, setSelectedGeos] = useState([]);
  const [selectedPersonas, setSelectedPersonas] = useState([]);
  const [reportKind, setReportKind] = useState('baseline'); // 'baseline' | 'change' | 'flash'
  const [timePeriod, setTimePeriod] = useState('Current');
  const [snapshotDate, setSnapshotDate] = useState(null); // baseline + custom
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [errors, setErrors] = useState({});
  const [jsonConfirmOpen, setJsonConfirmOpen] = useState(false);
  const [pendingSelections, setPendingSelections] = useState(null);

  const [filteredCategories, setFilteredCategories] = useState(categories);
  const [filteredLlms, setFilteredLlms] = useState(dbLlms);
  const [filteredMarkets, setFilteredMarkets] = useState(markets);
  const [filteredGeography, setFilteredGeography] = useState(geography);
  const [filteredPersonas, setFilteredPersonas] = useState(personas);
  // Unique LLM providers derived from filtered LLMs
  const filteredProviders = useMemo(() => {
    const seen = new Set();
    return filteredLlms
      .map((l) => l.llm_provider || 'Other')
      .filter((p) => !seen.has(p) && seen.add(p));
  }, [filteredLlms]);

  const getBrandEntry = (brandName) =>
    brandFilters.find((b) => b.brand_name === brandName) || null;

  const getCategoryEntry = (brandName, categoryName) => {
    const brand = getBrandEntry(brandName);
    return brand?.categories?.find((c) => c.categoryName === categoryName) || null;
  };

  const applyBrandData = (brandEntry, targetCategoryName = null) => {
    if (!brandEntry) return;
    const cats = (brandEntry.categories || []).map((c) => ({
      id: c.categoryId,
      name: c.categoryName,
    }));
    setFilteredCategories(cats);

    const catEntry = targetCategoryName
      ? brandEntry.categories?.find((c) => c.categoryName === targetCategoryName)
      : brandEntry.categories?.[0];

    setFilteredLlms(catEntry?.llms || []);
    setFilteredMarkets(catEntry?.markets || []);
    setFilteredGeography(catEntry?.geographies || []);
    setFilteredPersonas(catEntry?.personas || []);
  };

  // ── Pre-populate from Dashboard defaults when modal opens ─────────────────
  useEffect(() => {
    if (!open) return;
    setErrors({});

    const defaultBrand =
      brandItems.find((b) => b.id === defaults.brandId)?.name ||
      brandItems[0]?.name ||
      '';
    setSelectedBrand(defaultBrand);

    // Populate filtered options from brandFilters for the default brand
    if (brandFilters.length && defaultBrand) {
      const brandEntry = getBrandEntry(defaultBrand);
      const defaultCategoryName =
        categories.find((c) => c.id === (defaults.categoryIds?.[0]))?.name ||
        brandEntry?.categories?.[0]?.categoryName ||
        '';
      applyBrandData(brandEntry, defaultCategoryName || null);
      setSelectedCategory(defaultCategoryName);
    } else {
      // Fallback: use props directly
      setFilteredCategories(categories);
      setFilteredLlms(dbLlms);
      setFilteredMarkets(markets);
      setFilteredGeography(geography);
      setFilteredPersonas(personas);

      const defaultCat =
        categories.find((c) => c.id === (defaults.categoryIds?.[0]))?.name ||
        categories[0]?.name ||
        '';
      setSelectedCategory(defaultCat);
    }

    // LLM providers
    setSelectedProviders(defaults.llmProviders || []);

    // Markets
    const defaultMarkets = markets
      .filter((m) => (defaults.marketIds || []).includes(m.id))
      .map((m) => m.name);
    setSelectedMarkets(
      defaultMarkets.length ? defaultMarkets : [markets[0]?.name].filter(Boolean)
    );

    // Geographies
    const defaultGeos = geography
      .filter((g) => (defaults.geoIds || []).includes(g.id))
      .map((g) => g.name);
    setSelectedGeos(
      defaultGeos.length ? defaultGeos : [geography[0]?.name].filter(Boolean)
    );

    // Personas
    const defaultPersonas = personas
      .filter((p) => (defaults.personaIds || []).includes(p.id))
      .map((p) => p.name);
    setSelectedPersonas(defaultPersonas);

    // Report kind + date
    setReportKind('baseline');
    setTimePeriod(defaults.timePeriod || 'Current');
    setSnapshotDate(null);
    setStartDate(defaults.startDate || null);
    setEndDate(defaults.endDate || null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Brand change handler (cascades to all dependent dropdowns) ─────────────
  const handleBrandChange = (brandName) => {
    setSelectedBrand(brandName);
    setErrors((prev) => ({ ...prev, brand: false }));

    if (!brandFilters.length) return;

    const brandEntry = getBrandEntry(brandName);
    console.log("brandEntry",brandEntry)
    if (!brandEntry) return;

    const cats = (brandEntry.categories || []).map((c) => ({
      id: c.categoryId,
      name: c.categoryName,
    }));
    setFilteredCategories(cats);

    const firstCatEntry = brandEntry.categories?.[0];
    const firstCatName = firstCatEntry?.categoryName || '';

    setFilteredLlms(firstCatEntry?.llms || []);
    setFilteredMarkets(firstCatEntry?.markets || []);
    setFilteredGeography(firstCatEntry?.geographies || []);
    setFilteredPersonas(firstCatEntry?.personas || []);

    // Reset dependent selections to first available value
    setSelectedCategory(firstCatName);
    setSelectedProviders([]);
    setSelectedMarkets(firstCatEntry?.markets?.[0]?.name ? [firstCatEntry.markets[0].name] : []);
    setSelectedGeos(firstCatEntry?.geographies?.[0]?.name ? [firstCatEntry.geographies[0].name] : []);
    setSelectedPersonas([]);
    setErrors({});
  };

  // ── Category change handler (cascades to LLM/Market/Geo/Persona) ──────────
  const handleCategoryChange = (categoryName) => {
    setSelectedCategory(categoryName);
    setErrors((prev) => ({ ...prev, category: false }));

    if (!brandFilters.length) return;

    const catEntry = getCategoryEntry(selectedBrand, categoryName);
    if (!catEntry) return;

    setFilteredLlms(catEntry?.llms || []);
    setFilteredMarkets(catEntry?.markets || []);
    setFilteredGeography(catEntry?.geographies || []);
    setFilteredPersonas(catEntry?.personas || []);

    setSelectedProviders([]);
    setSelectedMarkets(catEntry?.markets?.[0]?.name ? [catEntry.markets[0].name] : []);
    setSelectedGeos(catEntry?.geographies?.[0]?.name ? [catEntry.geographies[0].name] : []);
    setSelectedPersonas([]);
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!selectedBrand) e.brand = true;
    if (!selectedCategory) e.category = true;
    if (!selectedProviders.length) e.llm = true;
    if (!selectedMarkets.length) e.markets = true;
    if (!selectedGeos.length) e.geos = true;
    if (!selectedPersonas.length) e.personas = true;
    if (timePeriod === 'Custom') {
      if (reportKind === 'baseline' && !snapshotDate) e.dates = true;
      if ((reportKind === 'change' || reportKind === 'flash') && (!startDate || !endDate)) e.dates = true;
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  // ── Build selections payload ────────────────────────────────────────────────
  const buildSelections = () => {
    const brandId = brandItems.find((b) => b.name === selectedBrand)?.id;
    const brandIds = brandId ? [brandId] : [];

    const categoryIds = filteredCategories
      .filter((c) => c.name === selectedCategory)
      .map((c) => c.id);

    const personaIds = filteredPersonas
      .filter((p) => selectedPersonas.includes(p.name))
      .map((p) => p.id);

    const marketIds = filteredMarkets
      .filter((m) => selectedMarkets.includes(m.name))
      .map((m) => m.id);

    const geoIds = filteredGeography
      .filter((g) => selectedGeos.includes(g.name))
      .map((g) => g.id);

    const globalLlmIds = filteredLlms
      .filter((l) => selectedProviders.includes(l.llm_provider || 'Other'))
      .map((l) => l.id);

    const llmIds = customerLlm
      .filter(
        (cl) =>
          globalLlmIds.includes(cl.llm_id) &&
          marketIds.includes(cl.market_id) &&
          geoIds.includes(cl.geography_id)
      )
      .map((cl) => cl.id);

    // Resolve dates based on reportKind
    let resolvedStartDate = null;
    let resolvedEndDate = null;
    if (timePeriod === 'Custom') {
      if (reportKind === 'baseline') {
        resolvedStartDate = snapshotDate;
        resolvedEndDate = snapshotDate;
      } else {
        resolvedStartDate = startDate;
        resolvedEndDate = endDate;
      }
    }

    const base = {
      brandIds,
      brandNames: [selectedBrand],
      categoryIds,
      category: selectedCategory,
      personaIds,
      persona: selectedPersonas,
      llmIds,
      llms: selectedProviders,
      marketIds,
      markets: selectedMarkets,
      geographyIds: geoIds,
      geographies: selectedGeos,
      reportKind: reportKind === 'flash' ? 'change' : reportKind,
      timePeriod,
      startDate: resolvedStartDate,
      endDate: resolvedEndDate,
    };

    // reportType only added for change-family reports; baseline payload stays untouched
    if (reportKind === 'flash') base.reportType = 'flash';

    return base;
  };

  const handleGenerate = () => {
    if (validate()) onGenerate(buildSelections());
  };

  const handleGenerateJson = () => {
    if (validate()) {
      setPendingSelections(buildSelections());
      setJsonConfirmOpen(true);
    }
  };

  const handleJsonConfirmClose = () => {
    if (jsonLoading) return;
    setJsonConfirmOpen(false);
    setPendingSelections(null);
  };

  const handleConfirmedJsonGenerate = (selections) => {
    if (!pendingSelections) return;
    setJsonConfirmOpen(false);
    onGenerateJson({ ...pendingSelections, ...selections });
  };

  const jsonExportOptions = useMemo(() => [
    {
      key: 'includeSentimentQuestions',
      icon: QuizOutlinedIcon,
      title: 'Include Sentiment Questions',
      description: 'Includes all brand sentiment questions and their LLM-generated responses across every tracked brand.',
      badge: 'LARGE DATASET',
    },
    {
      key: 'includeSources',
      icon: LinkOutlinedIcon,
      title: 'Include Sources',
      description: 'Includes full source URLs, source counts, classifications (Own, Competitors, PR Media, Third Party), and LLM-wise source breakdown.',
      badge: 'LARGE DATASET',
    },
  ], []);

  // Trim market/geo/persona to single when switching to flash
  useEffect(() => {
    if (reportKind === 'flash') {
      setSelectedMarkets((prev) => (prev.length > 1 ? [prev[0]] : prev));
      setSelectedGeos((prev) => (prev.length > 1 ? [prev[0]] : prev));
      setSelectedPersonas((prev) => (prev.length > 1 ? [prev[0]] : prev));
    }
  }, [reportKind]);

  // ── Multi-select chip renderer ─────────────────────────────────────────────
  const renderChips = (values, onRemove) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {values.map((v) => (
        <Chip
          key={v}
          label={v}
          size="small"
          onDelete={() => onRemove(v)}
          onMouseDown={(e) => e.stopPropagation()}
        />
      ))}
    </Box>
  );

  const multiHandler = (setter, errorKey) => (e) => {
    const v = e.target.value;
    setter(typeof v === 'string' ? v.split(',') : v);
    setErrors((prev) => ({ ...prev, [errorKey]: false }));
  };

  const removeChip = (setter) => (val) =>
    setter((prev) => prev.filter((x) => x !== val));

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <>
    <JsonExportConfirmModal
      open={jsonConfirmOpen}
      onClose={handleJsonConfirmClose}
      onConfirm={handleConfirmedJsonGenerate}
      loading={jsonLoading}
      options={jsonExportOptions}
      allowFullExport
      moduleKey="monitoring"
      metricLabels={MONITORING_METRIC_LABELS}
    />

    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Generate Report{brandName && ` — ${brandName}`}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>

          {/* ── Report Kind ── */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Report Type *
            </Typography>
            <ToggleButtonGroup
              value={reportKind}
              exclusive
              onChange={(_, val) => { if (val) setReportKind(val); }}
              size="small"
            >
              <ToggleButton value="baseline">Baseline Report</ToggleButton>
              <ToggleButton value="change" >Change Report</ToggleButton>
              <ToggleButton value="flash">Flash Report</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
              {reportKind === 'baseline'
                ? 'Generates a report for a single point-in-time snapshot.'
                : reportKind === 'change'
                ? 'Full comparison report between a recent date and a baseline/prior date.'
                : 'Concise summary of key changes and top insights between two dates.'}
            </Typography>
          </Box>

          {/* ── Brand (single select) ── */}
          <FormControl fullWidth error={!!errors.brand}>
            <InputLabel>Brand *</InputLabel>
            <Select
              value={selectedBrand}
              onChange={(e) => handleBrandChange(e.target.value)}
              label="Brand *"
            >
              <MenuItem value=""><em>Select a brand</em></MenuItem>
              {brandItems.map((b) => (
                <MenuItem key={b.id} value={b.name}>{b.name}</MenuItem>
              ))}
            </Select>
            {errors.brand && <FormHelperText>Please select a brand</FormHelperText>}
          </FormControl>

          {/* ── Category (single select) ── */}
          <FormControl fullWidth error={!!errors.category}>
            <InputLabel>Category *</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              label="Category *"
            >
              <MenuItem value=""><em>Select a category</em></MenuItem>
              {filteredCategories.map((c) => (
                <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
              ))}
            </Select>
            {errors.category && <FormHelperText>Please select a category</FormHelperText>}
          </FormControl>

          {/* ── LLM Provider (multi-select) ── */}
          <FormControl fullWidth error={!!errors.llm}>
            <InputLabel>LLM Provider *</InputLabel>
            <Select
              multiple
              value={selectedProviders}
              onChange={multiHandler(setSelectedProviders, 'llm')}
              input={<OutlinedInput label="LLM Provider *" />}
              renderValue={(selected) =>
                renderChips(selected, removeChip(setSelectedProviders))
              }
              MenuProps={MenuProps}
            >
              {filteredProviders.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
            {errors.llm && (
              <FormHelperText>Please select at least one LLM provider</FormHelperText>
            )}
          </FormControl>

          {/* ── Market (multi-select for baseline/change, single for flash) ── */}
          <FormControl fullWidth error={!!errors.markets}>
            <InputLabel>Market *</InputLabel>
            {reportKind === 'flash' ? (
              <Select
                value={selectedMarkets[0] || ''}
                onChange={(e) => {
                  setSelectedMarkets(e.target.value ? [e.target.value] : []);
                  setErrors((prev) => ({ ...prev, markets: false }));
                }}
                label="Market *"
                MenuProps={MenuProps}
              >
                {filteredMarkets.map((m) => (
                  <MenuItem key={m.id} value={m.name}>{m.name}</MenuItem>
                ))}
              </Select>
            ) : (
              <Select
                multiple
                value={selectedMarkets}
                onChange={multiHandler(setSelectedMarkets, 'markets')}
                input={<OutlinedInput label="Market *" />}
                renderValue={(selected) =>
                  renderChips(selected, removeChip(setSelectedMarkets))
                }
                MenuProps={MenuProps}
              >
                {filteredMarkets.map((m) => (
                  <MenuItem key={m.id} value={m.name}>{m.name}</MenuItem>
                ))}
              </Select>
            )}
            {errors.markets && (
              <FormHelperText>Please select at least one market</FormHelperText>
            )}
          </FormControl>

          {/* ── Geography (multi-select for baseline/change, single for flash) ── */}
          <FormControl fullWidth error={!!errors.geos}>
            <InputLabel>Geography *</InputLabel>
            {reportKind === 'flash' ? (
              <Select
                value={selectedGeos[0] || ''}
                onChange={(e) => {
                  setSelectedGeos(e.target.value ? [e.target.value] : []);
                  setErrors((prev) => ({ ...prev, geos: false }));
                }}
                label="Geography *"
                MenuProps={MenuProps}
              >
                {filteredGeography.map((g) => (
                  <MenuItem key={g.id} value={g.name}>{g.name}</MenuItem>
                ))}
              </Select>
            ) : (
              <Select
                multiple
                value={selectedGeos}
                onChange={multiHandler(setSelectedGeos, 'geos')}
                input={<OutlinedInput label="Geography *" />}
                renderValue={(selected) =>
                  renderChips(selected, removeChip(setSelectedGeos))
                }
                MenuProps={MenuProps}
              >
                {filteredGeography.map((g) => (
                  <MenuItem key={g.id} value={g.name}>{g.name}</MenuItem>
                ))}
              </Select>
            )}
            {errors.geos && (
              <FormHelperText>Please select at least one geography</FormHelperText>
            )}
          </FormControl>

          {/* ── Persona (multi-select for baseline/change, single for flash) ── */}
          <FormControl fullWidth error={!!errors.personas}>
            <InputLabel>Persona *</InputLabel>
            {reportKind === 'flash' ? (
              <Select
                value={selectedPersonas[0] || ''}
                onChange={(e) => {
                  setSelectedPersonas(e.target.value ? [e.target.value] : []);
                  setErrors((prev) => ({ ...prev, personas: false }));
                }}
                label="Persona *"
                MenuProps={MenuProps}
              >
                {filteredPersonas.map((p) => (
                  <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
                ))}
              </Select>
            ) : (
              <Select
                multiple
                value={selectedPersonas}
                onChange={multiHandler(setSelectedPersonas, 'personas')}
                input={<OutlinedInput label="Persona *" />}
                renderValue={(selected) =>
                  renderChips(selected, removeChip(setSelectedPersonas))
                }
                MenuProps={MenuProps}
              >
                {filteredPersonas.map((p) => (
                  <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
                ))}
              </Select>
            )}
            {errors.personas && (
              <FormHelperText>Please select at least one persona</FormHelperText>
            )}
          </FormControl>

          {/* ── Date range ── */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Date Range *
            </Typography>
            <ToggleButtonGroup
              value={timePeriod}
              exclusive
              onChange={(_, val) => {
                if (val) {
                  setTimePeriod(val);
                  setErrors((prev) => ({ ...prev, dates: false }));
                }
              }}
              size="small"
            >
              <ToggleButton value="Current">Current</ToggleButton>
              <ToggleButton value="Custom">Custom</ToggleButton>
            </ToggleButtonGroup>

            {timePeriod === 'Custom' && reportKind === 'baseline' && (
              <Stack sx={{ mt: 2 }}>
                <FormControl fullWidth error={!!errors.dates}>
                  <InputLabel>Snapshot Date</InputLabel>
                  <Select
                    value={snapshotDate || ''}
                    onChange={(e) => {
                      setSnapshotDate(e.target.value);
                      setErrors((prev) => ({ ...prev, dates: false }));
                    }}
                    label="Snapshot Date"
                  >
                    {sortedDates.map((d) => (
                      <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                  </Select>
                  {errors.dates && (
                    <FormHelperText>Please select a date</FormHelperText>
                  )}
                </FormControl>
              </Stack>
            )}

            {timePeriod === 'Custom' && (reportKind === 'change' || reportKind === 'flash') && (
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <FormControl fullWidth error={!!errors.dates}>
                  <InputLabel>Recent Date</InputLabel>
                  <Select
                    value={endDate || ''}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setErrors((prev) => ({ ...prev, dates: false }));
                    }}
                    label="Recent Date"
                  >
                    {sortedDates.map((d) => (
                      <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth error={!!errors.dates}>
                  <InputLabel>Baseline Date</InputLabel>
                  <Select
                    value={startDate || ''}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setErrors((prev) => ({ ...prev, dates: false }));
                    }}
                    label="Baseline Date"
                  >
                    {sortedDates.map((d) => (
                      <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                  </Select>
                  {errors.dates && (
                    <FormHelperText>Please select both dates</FormHelperText>
                  )}
                </FormControl>
              </Stack>
            )}
          </Box>

        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading || jsonLoading}>
          Cancel
        </Button>
        {user?.is_json_export_enabled !== false && (<Button
          onClick={handleGenerateJson}
          variant="outlined"
          color="primary"
          disabled={loading || jsonLoading}
        >
          {jsonLoading ? 'Downloading…' : 'Generate JSON'}
        </Button>)}
        <Button
          onClick={handleGenerate}
          variant="contained"
          color="primary"
          disabled={loading || jsonLoading}
        >
          {loading ? 'Starting…' : 'Generate HTML Report'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
